import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { Box, type BoxProps } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import { useDebouncedCallback } from 'use-debounce'
import { Defaults, PermissionArea, Text } from '@pbt/pbt-ui-components'

import type { PuiCKEditorHandle } from '~/components/common/ckeditor/PuiCKEditor'
import type { PuiTab } from '~/components/common/PuiTabs'
import PuiTabsPanel from '~/components/dashboard/soapV2/PuiTabsPanel'
import FeatureToggle from '~/constants/featureToggle'
import { fetchResolvedDocuments } from '~/store/actions/documents'
import { setDischargeNotes, setMedicalNotes } from '~/store/actions/soap'
import {
  getRecordingSoapId,
  setRecordingDischargeNotes,
  setRecordingMedicalNotes,
} from '~/store/duck/audioRecorder'
import {
  getCKEditorCurrentEditorTab,
  setCurrentEditorTab,
} from '~/store/duck/ckeditor'
import { getCRUDByArea } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import {
  getDocumentsError,
  getMultipleDocuments,
} from '~/store/reducers/documents'
import {
  getAppointmentId,
  getClientId,
  getDischargeNotes,
  getIsCurrentContextSoap,
  getIsFinalized,
  getMedicalNotes,
  getPatientId,
  getSoapId,
  getSOAPisFetching,
  getSOAPisLoading,
} from '~/store/reducers/soap'
import { EditorDocumentType, FeatureElements } from '~/types'

import PuiCKEditorMedicalDischargeNotes from './PuiCKEditorMedicalDischargeNotes'
import { ShareLanguageDropdown } from './ShareLanguageDropdown'
import { useAudioSummaryInjection } from './useAudioSummaryInjection'

const useStyles = makeStyles(
  (theme) => ({
    dividerContainer: {
      width: `calc(100% + ${theme.spacing(4)})`,
      marginLeft: theme.spacing(-2),
    },
    notesContainer: {
      border: theme.constants.tableBorder,
      backgroundColor: theme.colors.tableBackground,
    },
    tabContainer: {
      marginLeft: `-${theme.spacing(1)} !important`,
    },
  }),
  { name: 'MedicalDischargeNotesWidgetUnlocked' },
)

export interface MedicalDischargeNotesWidgetUnlockedProps {
  ContainerProps?: BoxProps
  container?: boolean
  defaultDischargeNotesTemplateId?: string
  defaultMedicalNotesTemplateId?: string
  hideElements?: FeatureElements[]
}

type CKEditorNotesTab = {
  id: string
  noteType: EditorDocumentType.SOAP_MEDICAL | EditorDocumentType.SOAP_DISCHARGE
  setAction: typeof setMedicalNotes | typeof setDischargeNotes
  subTitle: string
}

const MedicalDischargeNotesWidgetUnlocked = ({
  ContainerProps,
  container = true,
  hideElements,
  defaultDischargeNotesTemplateId,
  defaultMedicalNotesTemplateId,
}: MedicalDischargeNotesWidgetUnlockedProps) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const store = useStore()
  const { t } = useTranslation('Common')
  const editorRef = useRef<PuiCKEditorHandle>(null)
  const didSetDefaultDischargeNotesTemplate = useRef(false)
  const didSetDefaultMedicalNotesTemplate = useRef(false)
  const isSoapFinalized = useSelector(getIsFinalized)
  const isCurrentContextSoap = useSelector(getIsCurrentContextSoap)
  const soapId = useSelector(getSoapId)
  const soapIsLoading = useSelector(getSOAPisLoading)
  const soapIsFetching = useSelector(getSOAPisFetching)
  const SOAPPermissions = useSelector(getCRUDByArea(PermissionArea.SOAP))
  const tabValue = useSelector(getCKEditorCurrentEditorTab) || 0
  const clientId = useSelector(getClientId)
  const patientId = useSelector(getPatientId)
  const eventId = useSelector(getAppointmentId)
  const isDefaultSoapNotesTemplatesEnabled = useSelector(
    getFeatureToggle(FeatureToggle.SET_DEFAULT_SOAP_NOTE_TEMPLATES),
  )

  const isDischargeNotesTranslationEnabled = useSelector(
    getFeatureToggle(FeatureToggle.DISCHARGE_NOTES_TRANSLATION),
  )

  const { onEditorReady: onAudioSummaryEditorReady } = useAudioSummaryInjection(
    editorRef,
    tabValue,
  )

  const isReadOnly = isSoapFinalized || !isCurrentContextSoap
  const isSoapPending = soapIsLoading || soapIsFetching

  const medicalNotes = useSelector(getMedicalNotes)
  const dischargeNotes = useSelector(getDischargeNotes)

  const isAwoPmrRightRailEnabled = useSelector(
    getFeatureToggle(FeatureToggle.AWO_PMR_RIGHT_RAIL),
  )

  const templatesIds = [
    defaultMedicalNotesTemplateId,
    defaultDischargeNotesTemplateId,
  ].filter(Boolean) as string[]

  const hasDocumentError = useSelector(getDocumentsError)
  const documents = useSelector(getMultipleDocuments(templatesIds))

  useEffect(() => {
    if (templatesIds.length > 0 && isDefaultSoapNotesTemplatesEnabled) {
      const context = { clientId, patientId, eventId, soapId }
      dispatch(fetchResolvedDocuments(templatesIds, context, false))
    }
  }, [JSON.stringify(templatesIds), clientId, patientId, eventId, soapId])

  // Memoize panels array to prevent unnecessary re-executions of useCallback and useEffect
  // that depend on it. Only recreate when soapId, t, or action creators change.
  const panels: CKEditorNotesTab[] = useMemo(
    () => [
      {
        id: `${EditorDocumentType.SOAP_MEDICAL}_${soapId}`,
        subTitle: t('Common:NOT_SHARED_WITH_CLIENT'),
        noteType: EditorDocumentType.SOAP_MEDICAL,
        setAction: setMedicalNotes,
      },
      {
        id: `${EditorDocumentType.SOAP_DISCHARGE}_${soapId}`,
        subTitle: t('Common:SHARED_WITH_CLIENT'),
        noteType: EditorDocumentType.SOAP_DISCHARGE,
        setAction: setDischargeNotes,
      },
    ],
    [soapId, t, setMedicalNotes, setDischargeNotes],
  )

  const tabs: PuiTab[] = [
    {
      id: `${EditorDocumentType.SOAP_MEDICAL}_${soapId}`,
      label: t('Common:MEDICAL_NOTES'),
      hide: false,
    },
    {
      id: `${EditorDocumentType.SOAP_DISCHARGE}_${soapId}`,
      label: t('Common:DISCHARGE_NOTES'),
      hide: false,
    },
  ]

  const initializeEditorData = useCallback(() => {
    // Attempt pending audio summary injection now that the editor is ready.
    // This does NOT race with the template logic below because:
    //   1. replaceContent() returns false when the inner editor isn't mounted
    //      yet — the pending injection stays in Redux and retries on next call.
    //   2. When replaceContent() succeeds, the Model API fires onChange which
    //      updates Redux (medicalNotes/dischargeNotes become non-empty), so the
    //      template guard (notes.length === 0) prevents any overwrite.
    //   3. The didSetDefault*Template refs ensure templates are applied at most
    //      once per component lifecycle, providing a second safeguard.
    onAudioSummaryEditorReady()

    const panelConfig = panels[tabValue]

    // Set default templates if enabled and notes are empty
    if (
      documents.length > 0 &&
      !hasDocumentError &&
      isDefaultSoapNotesTemplatesEnabled &&
      editorRef.current
    ) {
      const medicalTemplate = documents.find(
        (document) => document?.id === defaultMedicalNotesTemplateId,
      )
      const dischargeTemplate = documents.find(
        (document) => document?.id === defaultDischargeNotesTemplateId,
      )
      if (
        medicalTemplate?.template?.body &&
        (medicalNotes === undefined || medicalNotes.length === 0) &&
        panelConfig.noteType === EditorDocumentType.SOAP_MEDICAL &&
        !didSetDefaultMedicalNotesTemplate.current
      ) {
        editorRef.current?.setEditorData(medicalTemplate.template.body)
        didSetDefaultMedicalNotesTemplate.current = true
      }
      if (
        dischargeTemplate?.template?.body &&
        (dischargeNotes === undefined || dischargeNotes.length === 0) &&
        panelConfig.noteType === EditorDocumentType.SOAP_DISCHARGE &&
        !didSetDefaultDischargeNotesTemplate.current
      ) {
        editorRef.current?.setEditorData(dischargeTemplate.template.body)
        didSetDefaultDischargeNotesTemplate.current = true
      }
    }
  }, [
    documents,
    tabValue,
    medicalNotes,
    dischargeNotes,
    panels,
    onAudioSummaryEditorReady,
  ])

  const handleTabChange = useCallback((newValue: number) => {
    dispatch(setCurrentEditorTab(newValue))
  }, [])

  const handleValueChange = useDebouncedCallback(
    (newValue: string, tabIndex: number) => {
      const tabConfig = panels[tabIndex]
      if (tabConfig) {
        dispatch(tabConfig.setAction(newValue))
      }
    },
    Defaults.DEBOUNCE_ACTION_TIME,
  )

  const isAudioRecorderEnabled = useSelector(
    getFeatureToggle(FeatureToggle.AUDIO_RECORDER),
  )

  useEffect(
    () => () => {
      handleValueChange.flush()
      if (!isAudioRecorderEnabled) return
      const state = store.getState()
      const recordingSoapId = getRecordingSoapId(state)
      if (recordingSoapId === soapId) {
        const medical = getMedicalNotes(state)
        const discharge = getDischargeNotes(state)
        if (medical != null) dispatch(setRecordingMedicalNotes(medical))
        if (discharge != null) dispatch(setRecordingDischargeNotes(discharge))
      }
    },
    [soapId, handleValueChange, dispatch, store, isAudioRecorderEnabled],
  )

  const computedHideElements = useMemo(
    () =>
      isAwoPmrRightRailEnabled
        ? Array.from(
            new Set([...(hideElements || []), FeatureElements.COMMENTS]),
          )
        : hideElements,
    [isAwoPmrRightRailEnabled, hideElements],
  )

  return (
    <Box
      className={classNames({
        [classes.notesContainer]: container,
      })}
      display="flex"
      id="notes-widget"
      minHeight={366}
      padding={2}
      width="100%"
      {...ContainerProps}
    >
      <PuiTabsPanel
        classes={{
          tabContainer: classes.tabContainer,
          dividerContainer: classes.dividerContainer,
        }}
        panels={panels}
        tabValue={tabValue}
        tabs={tabs}
        onChange={handleTabChange}
      >
        {({ tabPanel }) => (
          <>
            <Box alignItems="center" display="flex" gap={0.5} pb={0.5} pt={1.5}>
              <Text variant="lowAccent3">{tabPanel.subTitle}</Text>
              {tabPanel.noteType === EditorDocumentType.SOAP_DISCHARGE &&
                !isReadOnly &&
                SOAPPermissions.update &&
                !isSoapPending &&
                isDischargeNotesTranslationEnabled && (
                  <>
                    <Text variant="lowAccent3">&middot;</Text>
                    <ShareLanguageDropdown editorRef={editorRef} />
                  </>
                )}
            </Box>
            <PuiCKEditorMedicalDischargeNotes
              disabled={Boolean(
                isReadOnly || !SOAPPermissions.update || isSoapPending,
              )}
              editorReadyCallback={initializeEditorData}
              hideElements={computedHideElements}
              id={tabPanel.id}
              noteType={tabPanel.noteType}
              ref={editorRef}
              soapId={soapId}
              onChange={(value) => handleValueChange(value, tabValue)}
            />
          </>
        )}
      </PuiTabsPanel>
    </Box>
  )
}

export default MedicalDischargeNotesWidgetUnlocked
